import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { AxiosResponse } from "axios";
import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BloodLISReportView,
  SaveInternalNotes,
} from "Services/BloodLisResultData";
import { UpdateLisStatus } from "Services/MicrobiologyResultData";
import Status from "Shared/Common/Status";
import useLang from "Shared/hooks/useLanguage";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import { isJson } from "Utils/Common/Requisition";
import {
  StyledDropButton,
  StyledDropButtonThreeDots,
  StyledDropMenu,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import { savePdfUrls } from "../../../Redux/Actions/Index";
import PermissionComponent, {
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import { AddIcon, RemoveICon } from "../../../Shared/Icons";
import { dateFormatConversion } from "../../../Utils/Common/viewRequisitiontabs";
import { getActiveTabSortOrder, NormalizedData } from "./rowDataUtils";
import ViewContactsModal from "./Modal/ViewContactsModal";
import { useMicrobiologyResultDataContext } from "./ResultDataContext";
import ResultDataExpandableRow from "./ResultDataExpandableRow";

interface RowProps {
  RowData: any;
  tabsInfo?: any[];
}

const VALID_REPORT_STATUSES = [
  "final",
  "amended",
  "corrected",
  "prelim report",
  "ready to report",
];

const getStatusClassName = (status: string, t: (key: string) => string): string => {
  const statusMap: Record<string, string> = {
    [t("Specimen Collected")]: "badge-status-specimen-collected",
    [t("Processing")]: "badge-status-processing",
    [t("Result Available")]: "badge-status-result-available",
    [t("Complete")]: "badge-status-complete",
    [t("Deleted")]: "badge-status-deleted",
    [t("Validated")]: "badge-status-validated",
    [t("Save For Signature")]: "badge-status-waiting-for-Signature",
    [t("On Hold")]: "badge-status-hold",
    [t("Missing Info")]: "badge-status-missing-info",
    [t("In Transit")]: "badge-status-in-transit",
    [t("Canceled")]: "badge-status-canceled",
    [t("Approved")]: "badge-status-approved",
    [t("Pending")]: "badge-status-pending",
    [t("Rejected")]: "badge-status-rejected",
    [t("Shipped")]: "badge-status-shipped",
  };
  return statusMap[status] || "badge-status-default";
};

const getLisStatusClassName = (status: string, t: (key: string) => string): string => {
  const statusMap: Record<string, string> = {
    [t("Ready to Report")]: "badge-status-result-available",
    [t("Amended")]: "badge-status-complete",
    [t("Ready to Validate")]: "badge-status-validated",
    [t("Final")]: "badge-status-hold",
    [t("Corrected")]: "badge-status-in-transit",
    [t("Pending")]: "badge-status-pending",
    [t("Ready to Publish")]: "badge-status-shipped",
  };
  return statusMap[status] || "badge-status-default";
};

const Row: React.FC<RowProps> = ({ RowData, tabsInfo }) => {
  const requisitionOrderId = NormalizedData(RowData, "requisitionorderid");
  const requisitionId = NormalizedData(RowData, "requisitionid");
  const facilityId = NormalizedData(RowData, "facilityid");
  const columnDataJson = NormalizedData(RowData, "columndatajson");

  const { t } = useLang();
  const {
    selectedBox,
    setSelectedBox,
    filterData,
    loadGridData,
    rowsToExpand,
    setRowsToExpand,
    data,
    value,
  } = useMicrobiologyResultDataContext();
  const dispatch = useDispatch();
  const activeSortOrder = getActiveTabSortOrder(data.gridHeaders, value);

  const [viewContactsState, setViewContactsState] = useState<{
    show: boolean;
    id?: number;
  }>({ show: false });
  const [anchorEl, setAnchorEl] = useState({
    dropdown1: null,
    dropdown2: null,
    dropdown3: null,
    dropdown4: null,
  } as Record<string, any>);

  const handleClick = (event: React.MouseEvent, dropdownName: string) => {
    setAnchorEl((prev) => ({ ...prev, [dropdownName]: event.currentTarget }));
  };

  const handleClose = (dropdownName: string) => {
    setAnchorEl((prev) => ({ ...prev, [dropdownName]: null }));
  };

  const handleTestReport = async (reqOrderId: number) => {
    const lisStatus = NormalizedData(RowData, "lisstatus");
    const statusFound = VALID_REPORT_STATUSES.includes(
      (lisStatus || "").toLowerCase().trim()
    );
    if (!statusFound) {
      toast.error(t("Report is not available for this status"));
      return;
    }

    try {
      const res: AxiosResponse = await BloodLISReportView({
        requisitionOrderId: reqOrderId,
        isPreview: false,
        isCorrectedReport: false,
        isAmendedReport: false,
      });
      if (res?.data.statusCode === 200) {
        toast.success(t(res?.data?.message));
        loadGridData(false);
      } else if (res?.data.statusCode === 400) {
        toast.error(t(res?.data?.message));
      }
    } catch (err: any) {
      console.error("Error generating report:", err);
      toast.error(t("An error occurred while generating the report"));
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noreferrer");
  };

  const handleZebraPrint = () => {
    handleClose("dropdown1");
    toast.error(t("Printer functionality is not available."));
  };

  const handlePlated = async () => {
    handleClose("dropdown1");
    const tabId = filterData?.tabId;

    if (!requisitionOrderId || !tabId) {
      toast.error(t("Missing required data for Plated action"));
      return;
    }

    try {
      const response: AxiosResponse = await UpdateLisStatus({
        reqOrderId: requisitionOrderId,
        tabId,
      });
      if (response?.data?.statusCode === 200) {
        toast.success(t(response?.data?.message || "Status updated successfully"));
        loadGridData(false);
      } else {
        toast.error(t(response?.data?.message || "Failed to update status"));
      }
    } catch (error: any) {
      console.error("Error updating LIS status:", error);
      toast.error(t(error?.response?.data?.message || "Error updating status"));
    }
  };

  const handleViewContacts = () => {
    handleClose("dropdown1");
    if (facilityId) {
      setViewContactsState({ show: true, id: facilityId });
    }
  };

  const handleOpenOrderView = () => {
    handleClose("dropdown1");
    const tokenData = sessionStorage.getItem("userinfo");
    if (tokenData) {
      localStorage.setItem("userinfo", tokenData);
    }
    openInNewTab(
      `/OrderView/${btoa(requisitionId)}/${btoa(requisitionOrderId)}`
    );
  };

  const isRowExpanded = rowsToExpand?.includes(requisitionOrderId);
  const isChecked = activeSortOrder === 4
    ? selectedBox?.requisitionId?.find(
        (item: any) => item?.id === NormalizedData(RowData, "id")
      )
    : selectedBox?.requisitionId?.find(
        (item: any) => item?.requisitionOrderId === requisitionOrderId
      );

  const toggleRowExpand = () => {
    if (isRowExpanded) {
      setRowsToExpand(
        rowsToExpand.filter((val) => val !== requisitionOrderId)
      );
    } else {
      setRowsToExpand([...rowsToExpand, requisitionOrderId]);
    }
  };

  const columnData = isJson(columnDataJson)
    ? JSON.parse(columnDataJson)
    : null;

  const isPdfUrl = (url: string) =>
    url?.includes("https://truemedpo.blob.core.windows.net") ||
    url?.includes("https://digitalrequsitions.blob.core.windows.net");

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        {activeSortOrder !== 1 && (
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px"
            >
              {isRowExpanded ? (
                <button
                  onClick={toggleRowExpand}
                  id={`ResultDataHide_${requisitionOrderId}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                >
                  <RemoveICon />
                </button>
              ) : (
                <button
                  onClick={toggleRowExpand}
                  id={`ResultDataShow_${requisitionOrderId}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                >
                  <AddIcon />
                </button>
              )}
            </IconButton>
          </TableCell>
        )}

        <TableCell className="text-center">
          <div className="d-flex justify-content-center">
            <StyledDropButtonThreeDots
              id={`MicrobiologyResultDataActions_${requisitionOrderId}`}
              aria-controls={
                anchorEl.dropdown1
                  ? `actions-menu-${requisitionOrderId}`
                  : undefined
              }
              aria-haspopup="true"
              aria-expanded={anchorEl.dropdown1 ? "true" : undefined}
              onClick={(event) => handleClick(event, "dropdown1")}
              className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
            >
              <i className="bi bi-three-dots-vertical p-0 icon"></i>
            </StyledDropButtonThreeDots>
            <StyledDropMenuMoreAction
              id={`MicrobiologyResultDataActionsMenu_${requisitionOrderId}`}
              aria-labelledby={`actions-button-${requisitionOrderId}`}
              anchorEl={anchorEl.dropdown1}
              open={Boolean(anchorEl.dropdown1)}
              onClose={() => handleClose("dropdown1")}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              {activeSortOrder === 1 ? (
                <>
                  <PermissionComponent
                    moduleName="Microbiology LIS"
                    pageName="Result Data"
                    permissionIdentifier="View"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a onClick={handleOpenOrderView} className="w-auto p-0 text-dark">
                          <i className="fa fa-eye text-primary mr-2 w-20px"></i>
                          {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "View"))}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Microbiology LIS"
                    pageName="Result Data"
                    permissionIdentifier="ZebraPrint"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a onClick={handleZebraPrint} className="w-auto p-0 text-dark">
                          <i className="fa fa-print text-info mr-2 w-20px"></i>
                          {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Zebra Print"))}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Microbiology LIS"
                    pageName="Result Data"
                    permissionIdentifier="ViewContacts"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a onClick={handleViewContacts} className="w-auto p-0 text-dark">
                          <i className="fa fa-address-book text-secondary mr-2 w-20px"></i>
                          {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "View Contacts"))}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                  <PermissionComponent
                    moduleName="Microbiology LIS"
                    pageName="Result Data"
                    permissionIdentifier="Plated"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a onClick={handlePlated} className="w-auto p-0 text-dark">
                          <i className="fa fa-flask text-success mr-2 w-20px"></i>
                          {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "Plated"))}
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                </>
              ) : (
                <PermissionComponent
                  moduleName="Microbiology LIS"
                  pageName="Result Data"
                  permissionIdentifier="View"
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <MenuItem className="p-0">
                      <a onClick={handleOpenOrderView} className="w-auto p-0 text-dark">
                        <i className="fa fa-eye text-primary mr-2 w-20px"></i>
                        {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "View"))}
                      </a>
                    </MenuItem>
                  )}
                </PermissionComponent>
              )}
            </StyledDropMenuMoreAction>
          </div>
        </TableCell>

        {tabsInfo?.map((tabData: any) => {
          const columnKey = tabData?.columnKey;
          const rowId = `${columnKey}_${requisitionOrderId}`;
          const columnArray = NormalizedData(columnData, columnKey);

          if (Array.isArray(columnArray) && columnArray.length > 0) {
            const firstItem =
              columnArray[0]?.TestName || columnArray[0]?.InstrumentName || "";

            return (
              <TableCell key={rowId} id={rowId}>
                <HtmlTooltip
                  title={
                    <div>
                      {columnArray.map((item: any, idx: number) => (
                        <div key={idx}>
                          <span style={{ fontSize: "17px", fontWeight: "bold" }}>
                            {`${idx + 1}.`}
                          </span>
                          <span style={{ fontSize: "14px" }}>
                            {item?.TestName || item?.InstrumentName}
                          </span>
                        </div>
                      ))}
                    </div>
                  }
                  arrow
                >
                  <span>{firstItem}</span>
                </HtmlTooltip>
              </TableCell>
            );
          }

          if (
            !tabData.isShowOnUi ||
            tabData.isExpandData ||
            !tabData.isShow
          ) {
            return null;
          }

          const columnValue = NormalizedData(RowData, columnKey);
          const columnKeyLower = columnKey?.toLowerCase();

          if (columnKeyLower === "view") {
            return (
              <TableCell
                key={rowId}
                id={`BloodResultDataViewCell_${requisitionOrderId}`}
                className="text-center"
              >
                <div className="d-flex justify-content-center">
                  <PermissionComponent
                    moduleName="Microbiology LIS"
                    pageName="Result Data"
                    permissionIdentifier="View"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <button
                        id={`BloodResultDataViewButton_${requisitionOrderId}`}
                        role="link"
                        className="btn btn-sm fw-bold btn-warning fs-12px py-0 fw-500 align-items-center d-flex"
                        onClick={() => {
                          if (facilityId) {
                            setViewContactsState({ show: true, id: facilityId });
                          }
                        }}
                      >
                        {t(getPermissionDisplayName(pageName, moduleName, permissionIdentifier, "View"))}
                      </button>
                    )}
                  </PermissionComponent>
                </div>
              </TableCell>
            );
          }

          if (columnKeyLower === "report") {
            return (
              <TableCell
                key={rowId}
                id={`BloodResultDataReportCell_${requisitionOrderId}`}
                className="text-center"
              >
                <div className="d-flex justify-content-center">
                  <button
                    id={`BloodResultDataReportButton_${requisitionOrderId}`}
                    className="btn btn-sm fw-bold btn-excle fs-12px py-0 fw-500 align-items-center d-flex text-white"
                    onClick={() => handleTestReport(requisitionOrderId)}
                    disabled={
                      NormalizedData(RowData, "requisitionstatus") === "On Hold" ||
                      (NormalizedData(RowData, "isshowreportbutton") === false &&
                        activeSortOrder === 1)
                    }
                  >
                    {t("Report")}
                  </button>
                </div>
              </TableCell>
            );
          }

          if (columnKeyLower === "printlabel") {
            return (
              <TableCell key={rowId}>
                <div style={{ width: "max-content" }}>
                  <StyledDropButton
                    id={`BloodResultDataPrintLabelButton_${requisitionOrderId}`}
                    aria-controls={
                      anchorEl.dropdown4 ? "demo-positioned-menu4" : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={anchorEl.dropdown4 ? "true" : undefined}
                    onClick={(event) => handleClick(event, "dropdown4")}
                    className="btn btn-dark-brown btn-sm py-0 fw-400 fa-1x min-h-25px h-25px w-100px"
                  >
                    {t("Print Label")}
                    <span className="svg-icon svg-icon-5 m-0">
                      <ArrowBottomIcon />
                    </span>
                  </StyledDropButton>
                  <StyledDropMenu
                    id={`BloodResultDataPrintLabelMenu_${requisitionOrderId}`}
                    aria-labelledby="demo-positioned-button4"
                    anchorEl={anchorEl.dropdown4}
                    open={Boolean(anchorEl.dropdown4)}
                    onClose={() => handleClose("dropdown4")}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    className="w-auto"
                  >
                    <MenuItem disabled>
                      <i className="fa fa fa-print text-warning mr-2 w-20px"></i>
                      {t("No printers available")}
                    </MenuItem>
                  </StyledDropMenu>
                </div>
              </TableCell>
            );
          }

          if (isPdfUrl(columnValue)) {
            return (
              <TableCell
                key={rowId}
                id={`BloodResultDataDocsViewer_${requisitionOrderId}`}
              >
                <div className="d-flex justify-content-center">
                  <Link to={`/docs-viewer`} target="_blank">
                    <i
                      className="bi bi-file-earmark-pdf text-danger fa-2x cursor-pointer"
                      onClick={() => {
                        dispatch(savePdfUrls(columnValue));
                      }}
                    ></i>
                  </Link>
                </div>
              </TableCell>
            );
          }

          if (columnKeyLower === "requisitionstatus") {
            return (
              <TableCell
                key={rowId}
                id={`BloodResultDataStatus1_${requisitionOrderId}`}
                sx={{ width: "max-content", textAlign: "center" }}
              >
                <Status
                  cusText={columnValue}
                  cusClassName={getStatusClassName(columnValue, t)}
                />
              </TableCell>
            );
          }

          if (columnKeyLower === "lisstatus") {
            return (
              <TableCell
                key={rowId}
                id={`BloodResultDataStatus2_${requisitionOrderId}`}
                sx={{ width: "max-content", textAlign: "center" }}
              >
                <Status
                  cusText={columnValue}
                  cusClassName={getLisStatusClassName(columnValue, t)}
                />
              </TableCell>
            );
          }

          return (
            <TableCell key={rowId} id={`${columnKey}_${requisitionOrderId}`}>
              {dateFormatConversion(RowData, columnKey)}
            </TableCell>
          );
        })}
      </TableRow>

      <TableRow>
        <TableCell colSpan={13} className="padding-0">
          <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography gutterBottom component="div">
                <div className="row">
                  <div className="col-lg-12 bg-white">
                    <ResultDataExpandableRow
                      requisitionOrderId={requisitionOrderId}
                      requisitionId={requisitionId}
                      facilityId={facilityId}
                    />
                  </div>
                </div>
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <ViewContactsModal
        show={viewContactsState.show}
        onHide={() => {
          setViewContactsState({ show: false, id: undefined });
        }}
        id={viewContactsState.id}
      />
    </>
  );
};

export default React.memo(Row);

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));
